import React from "react";

import * as _ from "lodash";

import { StyleSheet, View, Dimensions, ScrollView, Platform } from "react-native";

import Utils, {
  hasItems,
  getStateData,
  escapeRegexp,
  sortByRelevance,
} from "../../../helpers/utils";
import AppConfig from "../../../config/AppConfig";

import Header from "../common/header";

import Search from "../common/search-bar/Search";
import TagsList from "./TagsList/TagsList";
import TagsMenuButton from "./TagsMenuButton/TagsMenuButton";
import BottomButton from "../common/BottomButton";
import ItemNotFound from "../common/ItemNotFound";
import CopyTagsFromActivitiesPanel from "./copy-tags/CopyTagsFromActivitiesPanel";
import LoadingIndicator from "../common/LoadingIndicator";
import appStyles from "../../appStyles";

const { height: HEIGHT, width: WIDTH } = Dimensions.get("window");

export default class TagsPage extends React.Component {
  constructor(props) {
    super(props);
    this.controller = props.controller;

    this.state = {
      isLoading: true,
      activity: {},
      allTags: [],
      tags: [],
      selectedTags: [],
      showCopyTagsPanel: false,
    };
  }

  componentDidMount = async () => {
    if (this.controller.hasNavigation()) {
      this.focusListenerUnsub = this.props.navigation.addListener(
        "focus",
        () => {
          this.refreshPage();
        },
      );
    } else {
      this.refreshPage();
    }
  };

  componentWillUnmount = async () => {
    if (this.focusListenerUnsub) this.focusListenerUnsub();
  };

  getArgs() {
    if (this.controller.hasNavigation()) {
      return this.props.route.params?.args;
    } else {
      return this.controller.state.screenArgs?.args?.args;
    }
  }

  refreshPage = async () => {
    const { activity } = this.getArgs() || {};

    if (!activity) {
      this.controller.changePage("feed", {
        selectedPage: "rn:activities",
        args: {},
      });
      return;
    }

    const allTags = getStateData(this.controller.state, "tags");

    let selectedTags = [];
    allTags.forEach((t) => {
      t.isSelected = activity.tags && activity.tags.includes(t.id);
      if (t.isSelected) selectedTags.push(t);
    });

    this.setState((state) => ({
      ...state,
      isLoading: false,
      activity: activity,
      selectedTags,
      allTags,
    }));
  };

  onRemoveTag = (tag) => {
    const { allTags, selectedTags } = this.state;
    const newSelectedTags = selectedTags.filter((t) => t.id !== tag.id);

    let tags = allTags.map((t) => {
      return {
        ...t,
        isSelected: t.id == tag.id ? false : t.isSelected,
      };
    });

    this.setState((state) => ({
      ...state,
      selectedTags: newSelectedTags,
      allTags: tags,
    }));
  };

  onSelectTag = (tag) => {
    const { selectedTags, allTags } = this.state;

    let tags = allTags.map((t) => {
      return {
        ...t,
        isSelected: t.id == tag.id ? true : t.isSelected,
      };
    });

    this.setState((state) => ({
      ...state,
      selectedTags: [...selectedTags, { ...tag, isSelected: true }],
      allTags: tags,
    }));
  };

  filterResults = ({ term = "" }) => {
    const { allTags } = this.state;
    let filteredTags = allTags;

    // Multi terms search
    const pattern = Utils.getSearchPattern(term);
    filteredTags = allTags.map((t) => {
      return {
        ...t,
        isHidden: term ? t.name && !t.name.match(pattern) : false,
      };
    });

    // If no tags found redirect to CREATE TAG page
    const foundCount = filteredTags.filter((t) => !t.isHidden).length;
    if (foundCount == 0) {
      this.setState((state) => ({
        ...state,
        allTags: [],
        notFoundText: term,
      }));
    } else {
      this.setState((state) => ({
        ...state,
        allTags: sortByRelevance(filteredTags, term),
        notFoundText: "",
      }));
    }
  };

  saveActivityTags = () => {
    const { activity, selectedTags } = this.state;

    this.controller.tracking.track("Edit Tag");
    this.controller.cardAction({
      action: "activity_tag",
      id_activity: activity.id,
      id_tag: selectedTags.map((t) => t.id),
      requestPage: "rn:activities",
      requestArgs: {},
    });
  };

  createTag = (name) => {
    const { activity } = this.state;

    this.controller.tracking.track("Edit Tag");
    this.controller.cardAction({
      action: "save",
      target: "tag",
      edit_name: name,
      edit_id: "",
      edit_is_active: "checked",
      source_id_activity: activity.id,
      redirectScreen: "feed",
      redirectPage: "rn:activities",
    });
  };

  goToEditTagPage = (name = "") => {
    const { activity } = this.state;

    this.controller.changePage("feed", {
      selectedPage: "rn:edit_tag",
      args: {
        tag: {
          activity: activity,
          source_id_activity: activity.id,
          edit_id: "",
          edit_name: name,
          edit_key: "",
          edit_icon: "",
          edit_parent: "",
          edit_parent_id: "",
          edit_is_active: "checked",
          edit_is_favorite: "",
        },
      },
    });
  };

  // ------------------------------------- COPY TAGS -------------------------------------
  toggleCopyTagsPanel = () => {
    this.setState((state) => ({
      ...state,
      showCopyTagsPanel: !this.state.showCopyTagsPanel,
    }));
  };

  // Users confirms to copy tags to activity
  addCopyTagsToActivity = (copyTags) => {
    const { activity, allTags, selectedTags } = this.state;

    let selectedTagsIds = selectedTags.map((t) => t.id);
    const newTagsIds = [...copyTags, ...selectedTagsIds];
    allTags.forEach((t) => (t.isSelected = newTagsIds.includes(t.id)));

    this.setState((state) => ({
      ...state,
      showCopyTagsPanel: false,
      allTags,
      selectedTags: allTags.filter((t) => t.isSelected),
      activity: {
        ...activity,
        tags: newTagsIds,
      },
    }));
  };

  render() {
    const {
      isLoading,
      allTags,
      activity,
      selectedTags,
      notFoundText,
      showDialog,
    } = this.state;
    const availableTags = allTags.filter((t) => !t.isHidden && !t.isSelected);

    return (
      <View style={styles.container}>
        <Header
          controller={this.controller}
          title="Add tags"
          icon={activity && activity.icon}
          returnPage="rn:activities"
        />

        {isLoading ? (
          <LoadingIndicator isLoading={isLoading} />
        ) : (
          <ScrollView style={styles.scrollView}>
            <TagsList
              title={activity.name}
              tags={selectedTags}
              hideCollapse
              showRemove
              onRemoveTag={this.onRemoveTag}
              initialNumToRender={5}
            />

            {hasItems(availableTags) ? (
              <TagsList
                title="All Tags"
                tags={availableTags}
                onSelectTag={this.onSelectTag}
                initialNumToRender={AppConfig.NUM_OF_TAGS}
              />
            ) : null}

            {notFoundText ? (
              <ItemNotFound
                name={notFoundText}
                type="tags"
                create={() => this.createTag(notFoundText)}
              />
            ) : null}
          </ScrollView>
        )}

        {!notFoundText ? (
          <TagsMenuButton
            addTag={this.goToEditTagPage}
            toggleCopyTagsPanel={this.toggleCopyTagsPanel}
          ></TagsMenuButton>
        ) : null}

        <View style={appStyles.footerOptions}>
          {!notFoundText ? (
            <Search
              filterResults={this.filterResults}
              placeholder="Search Tags or Create New"
              controller={this.controller}
            />
          ) : null}

          {!notFoundText ? (
            <BottomButton
              styles={styles}
              buttonText="Confirm Added Tags"
              onPress={this.saveActivityTags}
            />
          ) : null}
        </View>

        {this.state.showCopyTagsPanel ? (
          <CopyTagsFromActivitiesPanel
            controller={this.controller}
            selectedTagIds={selectedTags.map((t) => t.id)}
            toggleCopyTagsPanel={this.toggleCopyTagsPanel}
            saveCopyTags={this.addCopyTagsToActivity}
          />
        ) : null}
      </View>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    position: "relative",
    width: WIDTH,
    height: HEIGHT,
    margin: "auto",
    display: "flex",
    flexDirection: "column",
    flex: 1,
    borderRadius: 4,
    paddingBottom: 110,
    ...appStyles.maxWidth,
  },
  header: {
    fontFamily:  Platform.select({ default: "Nunito", android: "Nunito-SemiBold" }),
    fontWeight: "bold",
    fontSize: 20,
    lineHeight: 30,
    paddingLeft: 14,
    paddingTop: 10,
    display: "flex",
    alignItems: "center",
    letterSpacing: 1,
    color: "#333",
  },
  scrollView: {
    paddingHorizontal: 10,
  },
  bottomButton: {
    borderRadius: 25,
    backgroundColor: "#828282",
    shadowOpacity: 0.25,
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowColor: "#000",
    shadowRadius: 4,
    elevation: 5,
  },
  bottomButtonText: {
    //fontFamily: "SF Pro Text",
    //fontWeight: "bold",
    color: "#E0E0E0",
    fontSize: 16,
    lineHeight: 19,
    letterSpacing: 0.2,
    textAlign: "center",
  },
});
